import React, { useState } from 'react';
import { Job } from '../types';
import { ClockIcon, MapPinIcon, AlertCircleIcon, CheckCircleIcon, CalendarIcon, DollarSignIcon, PhoneIcon, MessageSquareIcon, FileTextIcon, ReceiptIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface JobCardProps {
  job: Job;
  onClick?: () => void;
  onAddCosts?: (e: React.MouseEvent) => void;
  onViewCalls?: (e: React.MouseEvent) => void;
  onViewMessages?: (e: React.MouseEvent) => void;
  onCreateQuote?: (e: React.MouseEvent) => void;
  onCreateInvoice?: (e: React.MouseEvent) => void;
  showAdminActions?: boolean;
}
const statusConfig = {
  requested: {
    label: 'Requested',
    color: 'bg-blue-500',
    icon: ClockIcon
  },
  scheduled: {
    label: 'Scheduled',
    color: 'bg-purple-500',
    icon: CalendarIcon
  },
  'in-progress': {
    label: 'In Progress',
    color: 'bg-orange-500',
    icon: AlertCircleIcon
  },
  complete: {
    label: 'Complete',
    color: 'bg-green-500',
    icon: CheckCircleIcon
  },
  paid: {
    label: 'Paid',
    color: 'bg-slate-500',
    icon: CheckCircleIcon
  }
};
const urgencyConfig = {
  standard: {
    label: 'Standard',
    color: 'text-slate-600'
  },
  urgent: {
    label: 'Urgent',
    color: 'text-orange-600'
  },
  emergency: {
    label: 'Emergency',
    color: 'text-red-600'
  }
};
const typeLabels = {
  automotive: 'Automotive',
  industrial: 'Industrial',
  residential: 'Residential',
  marine: 'Marine',
  custom: 'Custom'
};
export function JobCard({
  job,
  onClick,
  onAddCosts,
  onViewCalls,
  onViewMessages,
  onCreateQuote,
  onCreateInvoice,
  showAdminActions = false
}: JobCardProps) {
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const status = statusConfig[job.status];
  const StatusIcon = status.icon;
  const urgency = urgencyConfig[job.urgency];
  const totalCosts = job.costs.reduce((sum, c) => sum + c.amount, 0);
  const handleCreateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCreateMenu(!showCreateMenu);
  };
  const handleQuoteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCreateMenu(false);
    onCreateQuote?.(e);
  };
  const handleInvoiceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCreateMenu(false);
    onCreateInvoice?.(e);
  };
  return <motion.div whileTap={{
    scale: 0.98
  }} onClick={onClick} className="bg-white rounded-lg shadow-sm border-2 border-slate-100 overflow-hidden transition-shadow hover:shadow-md cursor-pointer relative p-3" data-id="element-101">
      <div className="flex items-start justify-between gap-3 mb-2" data-id="element-102">
        <div className="flex-1 min-w-0" data-id="element-103">
          <div className="flex items-center gap-2 mb-1" data-id="element-104">
            <h3 className="font-bold text-slate-900 truncate" data-id="element-105">{job.title}</h3>
            <span className="text-xs text-slate-500 flex-shrink-0" data-id="element-106">
              • {typeLabels[job.type]}
            </span>
          </div>
          <p className="text-sm text-slate-600 line-clamp-1" data-id="element-107">
            {job.description}
          </p>
        </div>
        <motion.div whileHover={{
        scale: 1.05
      }} className={`flex items-center gap-1 px-2 py-1 ${status.color} rounded-full flex-shrink-0`} data-id="element-108">
          <StatusIcon className="w-3 h-3 text-white" data-id="element-109" />
          <span className="text-xs font-bold text-white" data-id="element-110">{status.label}</span>
        </motion.div>
      </div>

      <div className="flex items-center gap-3 text-xs text-slate-500 mb-2" data-id="element-111">
        <div className="flex items-center gap-1" data-id="element-112">
          <MapPinIcon className="w-3 h-3" data-id="element-113" />
          <span data-id="element-114">{job.location}</span>
        </div>
        <div className={`flex items-center gap-1 font-semibold ${urgency.color}`} data-id="element-115">
          <AlertCircleIcon className="w-3 h-3" data-id="element-116" />
          <span data-id="element-117">{urgency.label}</span>
        </div>
        {totalCosts > 0 && <>
            <span data-id="element-118">•</span>
            <div className="flex items-center gap-1" data-id="element-119">
              <DollarSignIcon className="w-3 h-3" data-id="element-120" />
              <span className="font-semibold" data-id="element-121">
                ${totalCosts.toLocaleString()}
              </span>
            </div>
          </>}
        {job.scheduledDate && <>
            <span data-id="element-122">•</span>
            <div className="flex items-center gap-1" data-id="element-123">
              <CalendarIcon className="w-3 h-3" data-id="element-124" />
              <span data-id="element-125">
                {new Date(job.scheduledDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            })}
              </span>
            </div>
          </>}
      </div>

      {showAdminActions && <div className="space-y-2 pt-2 border-t border-slate-100" data-id="element-126">
          {/* Communication Buttons */}
          <div className="grid grid-cols-2 gap-2" data-id="element-127">
            <motion.button whileTap={{
          scale: 0.95
        }} onClick={e => {
          e.stopPropagation();
          onViewCalls?.(e);
        }} className="px-2 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md font-semibold text-xs flex items-center justify-center gap-1 transition-colors" data-id="element-128">
              <PhoneIcon className="w-3 h-3" data-id="element-129" />
              Calls
            </motion.button>
            <motion.button whileTap={{
          scale: 0.95
        }} onClick={e => {
          e.stopPropagation();
          onViewMessages?.(e);
        }} className="px-2 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md font-semibold text-xs flex items-center justify-center gap-1 transition-colors" data-id="element-130">
              <MessageSquareIcon className="w-3 h-3" data-id="element-131" />
              Messages
            </motion.button>
          </div>

          {/* Create Button with Dropdown */}
          <div className="relative" data-id="element-132">
            <motion.button whileTap={{
          scale: 0.95
        }} onClick={handleCreateClick} className="w-full px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-semibold text-xs flex items-center justify-center gap-1.5 transition-colors" data-id="element-133">
              <FileTextIcon className="w-3 h-3" data-id="element-134" />
              Create
            </motion.button>

            {/* Dropdown Menu */}
            <AnimatePresence data-id="element-135">
              {showCreateMenu && <motion.div initial={{
            opacity: 0,
            y: -10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -10,
            scale: 0.95
          }} transition={{
            duration: 0.15
          }} className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-xl border-2 border-slate-200 overflow-hidden z-10" data-id="element-136">
                  <motion.button whileTap={{
              scale: 0.98
            }} onClick={handleQuoteClick} className="w-full px-3 py-2 hover:bg-slate-50 text-left flex items-center gap-2 transition-colors border-b border-slate-100" data-id="element-137">
                    <FileTextIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-138" />
                    <span className="font-semibold text-sm text-slate-900" data-id="element-139">
                      Create Quote
                    </span>
                  </motion.button>
                  <motion.button whileTap={{
              scale: 0.98
            }} onClick={handleInvoiceClick} className="w-full px-3 py-2 hover:bg-slate-50 text-left flex items-center gap-2 transition-colors" data-id="element-140">
                    <ReceiptIcon className="w-3.5 h-3.5 text-green-600" data-id="element-141" />
                    <span className="font-semibold text-sm text-slate-900" data-id="element-142">
                      Create Invoice
                    </span>
                  </motion.button>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Add Costs Button */}
          {onAddCosts && <motion.button whileTap={{
        scale: 0.95
      }} onClick={e => {
        e.stopPropagation();
        onAddCosts(e);
      }} className="w-full px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-md font-semibold text-xs flex items-center justify-center gap-1.5 transition-colors" data-id="element-143">
              <DollarSignIcon className="w-3 h-3" data-id="element-144" />
              Add Costs
            </motion.button>}
        </div>}
    </motion.div>;
}